import React from 'react';

/**
 * 複数月のメトリクスを並べて比較表示するコンポーネント
 * @param {Object} multiMonthData - { thisMonth, lastMonth, twoMonthsAgo }
 */
function MultiMonthComparison({ multiMonthData = {}, mode = 'default' }) {
  if (!multiMonthData.thisMonth) {
    return null;
  }

  const { thisMonth, lastMonth, twoMonthsAgo } = multiMonthData;
  const isAttendancePerDayMode = mode === 'attendance-per-day';
  const thisMonthMetrics = thisMonth.metrics || [];

  const hasTargetInput = (metric) => {
    const targetsMap = thisMonth.targets || {};
    const targetItems = thisMonth.target_items || [];

    if (targetsMap[metric] !== undefined && targetsMap[metric] !== null && targetsMap[metric] !== '') {
      return true;
    }

    const prefixed = `target_${metric}`;
    if (targetsMap[prefixed] !== undefined && targetsMap[prefixed] !== null && targetsMap[prefixed] !== '') {
      return true;
    }

    const normalizedMetric = String(metric).toLowerCase();
    const item = targetItems.find((row) => {
      const key = String(row.target_key || '').toLowerCase();
      const label = String(row.target_label || '').toLowerCase();
      return key === prefixed || key === normalizedMetric || label === normalizedMetric || label.includes(normalizedMetric);
    });

    if (!item) {
      return false;
    }

    return item.target_value !== undefined && item.target_value !== null && item.target_value !== '';
  };

  const allMetrics = Array.from(
    new Set([
      ...thisMonthMetrics,
      ...(lastMonth?.metrics || []),
      ...(twoMonthsAgo?.metrics || []),
    ])
  );

  const visibleMetrics = allMetrics.filter(metric => {
    const thisVal = parseFloat(thisMonth.summary?.[metric] || 0);

    if (thisVal === 0 && !hasTargetInput(metric)) {
      return false;
    }

    const lastVal = parseFloat(lastMonth?.summary?.[metric] || 0);
    const twoVal = parseFloat(twoMonthsAgo?.summary?.[metric] || 0);
    return thisVal !== 0 || lastVal !== 0 || twoVal !== 0 || hasTargetInput(metric);
  });

  const sortedVisibleMetrics = [...visibleMetrics].sort((a, b) => {
    const valueA = parseFloat(thisMonth.summary?.[a] || 0);
    const valueB = parseFloat(thisMonth.summary?.[b] || 0);

    if (valueB !== valueA) {
      return valueB - valueA;
    }

    return String(a).localeCompare(String(b), 'ja');
  });

  const resolveTargetValue = (metric) => {
    const targetsMap = thisMonth.targets || {};
    const targetItems = thisMonth.target_items || [];

    if (targetsMap[metric] != null) {
      return parseFloat(targetsMap[metric] || 0);
    }

    const prefixed = `target_${metric}`;
    if (targetsMap[prefixed] != null) {
      return parseFloat(targetsMap[prefixed] || 0);
    }

    const normalizedMetric = String(metric).toLowerCase();
    const item = targetItems.find((row) => {
      const key = String(row.target_key || '').toLowerCase();
      const label = String(row.target_label || '').toLowerCase();
      return key === prefixed || key === normalizedMetric || label === normalizedMetric || label.includes(normalizedMetric);
    });

    return parseFloat(item?.target_value || 0);
  };

  const formatPerDayValue = (value) => {
    if (!Number.isFinite(value)) {
      return '-';
    }

    return Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  if (sortedVisibleMetrics.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-6">
      <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-6">
        月別比較（目標 / 当月 / 前月 / 前々月）
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedVisibleMetrics.map(metric => {
          const targetVal = resolveTargetValue(metric);
          const thisVal = parseFloat(thisMonth.summary?.[metric] || 0);
          const lastVal = parseFloat(lastMonth?.summary?.[metric] || 0);
          const twoVal = parseFloat(twoMonthsAgo?.summary?.[metric] || 0);

          const targetDivisor = Number(thisMonth.shift_registration_count || 0);
          const thisDivisor = Number(thisMonth.attendance_work_days || 0);
          const lastDivisor = Number(lastMonth?.attendance_work_days || 0);
          const twoDivisor = Number(twoMonthsAgo?.attendance_work_days || 0);

          const targetPerDay = targetDivisor > 0 ? targetVal / targetDivisor : null;
          const thisPerDay = thisDivisor > 0 ? thisVal / thisDivisor : null;
          const lastPerDay = lastDivisor > 0 ? lastVal / lastDivisor : null;
          const twoPerDay = twoDivisor > 0 ? twoVal / twoDivisor : null;

          const diffVsLast = thisVal - lastVal;
          const diffPercentVsLast = lastVal !== 0 ? ((diffVsLast / lastVal) * 100).toFixed(1) : 0;
          const isPositiveVsLast = diffVsLast >= 0;
          const achievementRate = targetVal > 0 ? ((thisVal / targetVal) * 100).toFixed(1) : null;

          return (
            <div key={metric} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <h4 className="text-sm font-semibold text-gray-700 mb-4">{metric}</h4>

              <div className="grid grid-cols-4 gap-2 mb-3">
                <div className="bg-yellow-50 rounded p-2">
                  <p className="text-xs text-gray-600 mb-1">目標</p>
                  <p className="text-lg font-bold text-yellow-700">{targetVal.toLocaleString()}</p>
                  {isAttendancePerDayMode && (
                    <p className="text-[10px] text-gray-500 mt-1">
                      {formatPerDayValue(targetPerDay)}
                    </p>
                  )}
                </div>

                <div className="bg-blue-50 rounded p-2">
                  <p className="text-xs text-gray-600 mb-1">当月</p>
                  <p className="text-lg font-bold text-blue-600">{thisVal.toLocaleString()}</p>
                  {isAttendancePerDayMode && (
                    <p className="text-[10px] text-gray-500 mt-1">
                      {formatPerDayValue(thisPerDay)}
                    </p>
                  )}
                </div>

                <div className="bg-gray-50 rounded p-2">
                  <p className="text-xs text-gray-600 mb-1">前月</p>
                  <p className="text-lg font-bold text-gray-600">{lastVal.toLocaleString()}</p>
                  {isAttendancePerDayMode && (
                    <p className="text-[10px] text-gray-500 mt-1">
                      {formatPerDayValue(lastPerDay)}
                    </p>
                  )}
                </div>

                <div className="bg-gray-50 rounded p-2">
                  <p className="text-xs text-gray-600 mb-1">前々月</p>
                  <p className="text-lg font-bold text-gray-600">{twoVal.toLocaleString()}</p>
                  {isAttendancePerDayMode && (
                    <p className="text-[10px] text-gray-500 mt-1">
                      {formatPerDayValue(twoPerDay)}
                    </p>
                  )}
                </div>
              </div>

              <div className="border-t pt-3 space-y-2">
                {lastMonth && (
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-600">前月比</p>
                    <div
                      className={`flex items-center gap-1 font-semibold text-sm ${
                        isPositiveVsLast ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      <span>{isPositiveVsLast ? '↑' : '↓'}</span>
                      <span>{Math.abs(diffVsLast).toLocaleString()}</span>
                      <span>({diffPercentVsLast}%)</span>
                    </div>
                  </div>
                )}
                {achievementRate !== null && (
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-600">達成率</p>
                    <p className="font-semibold text-sm text-yellow-700">{achievementRate}%</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MultiMonthComparison;
